import { generatePath, type NavigateFunction } from "react-router";

import {
  AssociationType,
  fileAssociationType,
} from "@/shared/domain/association";
import {
  rulePrefillToSearchParams,
  type IngestionRulePrefill,
} from "@/shared/domain/ingestionRules/ingestionRulePrefill";
import { SearchParams } from "@/shared/environment";
import { LoggerService } from "@/shared/services/LoggerService";
import { nanoSecToSecStr } from "@/shared/time";

import { layoutNavigationState } from "./layoutNavigation";
import {
  ActionRouteParams,
  ActionsRouteParam,
  AgentThreadRouteParams,
  AssociationRouteParams,
  CollectionRouteParams,
  CreateSessionLocationState,
  CreateSessionRouteParams,
  DatasetRouteParams,
  DeviceRouteParams,
  FileRouteParams,
  InviteRouteParams,
  InvocationRouteParams,
  InvokeActionRouteParams,
  LinkSlackRouteParams,
  SessionRouteParams,
  SignInRouteParams,
  TriggerRouteParams,
  VisualizerRouteParams,
} from "./types";

/**
 * The widget-editor path tails, relative to a metrics-dashboard route. Route
 * registration, the editor's `useMatch` patterns, and
 * {@link Goto.createMetricsWidgetRoute} all build from these, so the app can
 * never navigate to a URL no route matches.
 */
export const WIDGET_ROUTE_TAIL = Object.freeze({
  create: "widgets/new",
  edit: "widgets/:cardId/edit",
});

/**
 * The absolute route patterns (`:params` left unfilled) for the widget editor
 * under one dashboard context — the device-scoped tab when `deviceScoped`, the
 * org-level page otherwise.
 *
 * Each door has a pattern naming a dashboard and one without, for the unsaved
 * view, which has no record id. A saved dashboard is always named, so a
 * mid-edit reload restores the draft against it rather than against whichever
 * dashboard the landing heuristic picks. The literal `widgets` segment's
 * position keeps the four patterns from matching each other's URLs.
 */
export function metricsWidgetRoutePatterns(opts: { deviceScoped: boolean }): {
  create: string;
  createWithoutDashboard: string;
  edit: string;
  editWithoutDashboard: string;
} {
  const metrics = opts.deviceScoped ? "/devices/:deviceId/metrics" : "/metrics";
  const dashboard = `${metrics}/:dashboardId`;
  return {
    create: `${dashboard}/${WIDGET_ROUTE_TAIL.create}`,
    createWithoutDashboard: `${metrics}/${WIDGET_ROUTE_TAIL.create}`,
    edit: `${dashboard}/${WIDGET_ROUTE_TAIL.edit}`,
    editWithoutDashboard: `${metrics}/${WIDGET_ROUTE_TAIL.edit}`,
  };
}

/** A section of the agent form that a link can open the form on. */
export type AgentFormSection = "name" | "goals" | "skills" | "model";

/**
 * The id on an agent form section's heading, which an edit link names as its
 * hash. Prefixed, because bare `name` and `model` ids already exist elsewhere
 * in the app.
 */
export function agentFormSectionId(section: AgentFormSection): string {
  return `agent-${section}`;
}

/** `path` with each `/`-separated segment URL-encoded, so a folder name cannot break the URL. */
export function encodePath(path: string): string {
  return path.split("/").map(encodeURIComponent).join("/");
}

/**
 * `route` with `directoryPath` appended as encoded segments, or `route` alone
 * when there is no directory path.
 */
function withDirectoryPath(
  route: string,
  directoryPath: string | undefined,
): string {
  return directoryPath ? `${route}/${encodePath(directoryPath)}` : route;
}

export class Goto {
  static route = Object.freeze({
    Account: "/account",
    ActionHub: "/actions/hub",
    ActionImages: "/actions/images",
    Actions: "/actions",
    Admin: "/admin",
    AgentSkills: "/agents/skills",
    AgentThreads: "/agents/threads",
    Agents: "/agents",
    Collections: "/collections",
    Comments: "/comments",
    CreateAction: "/actions/create",
    CreateAgent: "/agents/create",
    CreateDataset: "/datasets/create",
    CreateSession: "/sessions/create",
    CreateTrigger: "/triggers/create",
    Datasets: "/datasets",
    /**
     * Finds a device by its `dv_` ID and redirects to its Configurations tab,
     * at the folder the rest of the URL names. A file associated with a device
     * carries only the `dv_` ID, while the device page is addressed by
     * `device_id`, which the org chooses.
     */
    DeviceConfigurationsByUniversalId: "/device-configurations",
    /**
     * Opens the Assets tab of Settings at the folder the rest of the URL
     * names, when the `og_` ID in the URL is the current organization's. The
     * Assets tab shows only the current organization's files, so a link to
     * another organization's files has to say so instead.
     */
    OrganizationAssetsById: "/organization-assets",
    DeviceTypes: "/device-types",
    Devices: "/devices",
    Events: "/events",
    Files: "/files",
    Home: "/",
    /**
     * Where Slack redirects the installing admin's browser. This string is one of four copies
     * that must agree, and the only one the SPA owns: the others are `OAUTH_CALLBACK_PATH` in
     * `roboto_service.domain.slack.oauth`, the `redirect_urls` entry in that package's
     * `manifest.yaml`, and the URL registered in each environment's live Slack app. Nothing
     * enforces agreement across the language boundary — drift shows up only as Slack refusing
     * the install with `bad_redirect_uri`.
     */
    SlackIntegrationCallback: "/integrations/slack/callback",
    Invite: "/invites",
    /**
     * Completes a Slack account link. The path is baked into the URLs the backend mints and Slack
     * has already delivered, so changing it strands every link in flight.
     */
    LinkSlack: "/link-slack",
    Invocations: "/invocations",
    InvokeAction: "/actions/invoke",
    Metrics: "/metrics",
    Notifications: "/notifications",
    Orgs: "/orgs",
    RobotoUriRedirect: "/open",
    Search: "/search",
    Sessions: "/sessions",
    Settings: {
      Root: "/settings",
      Organization: "/settings/organization",
      Tokens: "/settings/tokens",
      Usage: "/settings/usage",
      AI: "/settings/ai",
      Secrets: "/settings/secrets",
      Sessions: "/settings/sessions",
      /**
       * Like {@link Integrations}: the rules list is a settings tab, and each
       * rule's own pages (detail, create, edit) are top-level routes under
       * `Rules`.
       */
      Ingestion: {
        /** The tab listing the org's ingestion rules. */
        Root: "/settings/ingestion",
        Rules: "/ingestion",
      },
      /**
       * Reached through Settings, but only `Root` actually lives under
       * `/settings`: each integration's own page is a top-level route, so that
       * Slack's OAuth callback and S3's bucket explorer sit beside it rather
       * than nested inside a settings tab. They are grouped here because that
       * is how a reader navigates to them, not because the URLs share a prefix.
       */
      Integrations: {
        /** The catalogue tab, listing every integration this deployment offers. */
        Root: "/settings/integrations",
        Foxglove: "/integrations/foxglove",
        S3: "/integrations/s3",
        /**
         * Registered only where `Env.slackEnabled`; elsewhere this URL falls
         * through to the router's catch-all. Link to {@link Root} instead when
         * the caller cannot tell whether the deployment serves Slack.
         */
        Slack: "/integrations/slack",
      },
      CustomFields: "/settings/custom-fields",
      /**
       * The tab that browses the files associated with the organization
       * itself rather than with a dataset or device.
       */
      Assets: "/settings/assets",
    },
    SignIn: "/signin",
    SignUp: "/signup",
    Triggers: "/triggers",
    Unauthorized: "/unauthorized",
    Users: "/users",
    Visualize: "/visualize",
  } as const);

  #navigate: NavigateFunction;

  constructor(navigator: NavigateFunction) {
    this.#navigate = navigator;
  }

  /**
   * Navigate to an arbitrary path. Intended for use by the AI navigate tool
   * for routes that don't have a dedicated method.
   */
  public navigateTo(path: string) {
    void this.#navigate(path);
  }

  public account() {
    void this.#navigate(Goto.route.Account);
  }

  public home() {
    void this.#navigate(Goto.route.Home);
  }

  public datasets() {
    void this.#navigate(Goto.route.Datasets);
  }

  public devices() {
    void this.#navigate(Goto.route.Devices);
  }

  public agents() {
    void this.#navigate(Goto.route.Agents);
  }

  public agent(agentId: string) {
    void this.#navigate(Goto.createAgentRoute(agentId));
  }

  public comments() {
    void this.#navigate(Goto.route.Comments);
  }

  public signIn(params: SignInRouteParams = {}) {
    const { inviteId, slackLinkNonce, chooseOrg, emailVerified } = params;

    if (inviteId && chooseOrg) {
      LoggerService.warn(
        "Both inviteId and chooseOrg are set. Ignoring chooseOrg",
      );
    }

    const query = new URLSearchParams();
    if (inviteId) {
      query.set("inviteId", inviteId);
    } else if (slackLinkNonce) {
      // Carried as its own parameter rather than a general return-to URL: a nonce is opaque and can
      // only ever be interpolated into this one route, so nothing here has to be validated against
      // an allowlist the way an arbitrary destination would.
      query.set("slackLinkNonce", slackLinkNonce);
    } else if (chooseOrg) {
      query.set("chooseorg", "true");
    }
    if (emailVerified) {
      query.set("emailVerified", "true");
    }

    const search = query.toString();
    void this.#navigate(
      search ? `${Goto.route.SignIn}?${search}` : Goto.route.SignIn,
    );
  }

  public linkSlack(params: LinkSlackRouteParams) {
    void this.#navigate(
      `${Goto.route.LinkSlack}/${encodeURIComponent(params.nonce)}`,
    );
  }

  public signUp() {
    void this.#navigate(Goto.route.SignUp);
  }

  public invite(params: InviteRouteParams = {}) {
    const { inviteId } = params;
    if (inviteId) {
      void this.#navigate(`${Goto.route.Invite}/${inviteId}`);
    } else {
      void this.#navigate(Goto.route.Invite);
    }
  }

  public search(urlParams?: URLSearchParams) {
    if (urlParams) {
      void this.#navigate(`${Goto.route.Search}?${urlParams.toString()}`);
      return;
    }
    void this.#navigate(Goto.route.Search);
  }

  public static createVisualizerRoute(workspaceId?: string): string {
    return generatePath("/visualize/:workspaceId?", {
      workspaceId: workspaceId ?? null,
    });
  }

  /**
   * Skill detail page — where a skill is read and edited.
   *
   * Omitting `version` (or passing null) targets the skill itself, which
   * resolves to whatever its latest version is when the page loads. Passing
   * one pins that exact version, and the segment is spelled `v<N>` so the URL
   * reads the way versions are written everywhere else in the product.
   */
  public static createSkillRoute(
    skillId: string,
    version?: number | null,
  ): string {
    const base = `${Goto.route.AgentSkills}/${skillId}`;
    return version === undefined || version === null
      ? base
      : `${base}/versions/v${version}`;
  }

  /** Agent detail page — what every link to an agent lands on. */
  public static createAgentRoute(agentId: string): string {
    return `${Goto.route.Agents}/${agentId}`;
  }

  /**
   * The agent's edit form, the only editable surface an agent has. Naming a
   * `section` opens the form scrolled to that section's heading.
   */
  public static createEditAgentRoute(
    agentId: string,
    section?: AgentFormSection,
  ): string {
    const base = `${Goto.createAgentRoute(agentId)}/edit`;
    return section === undefined
      ? base
      : `${base}#${agentFormSectionId(section)}`;
  }

  /** Threads tab of the agent detail UI — the table of threads launched
   *  from this agent. */
  public static createAgentThreadsRoute(agentId: string): string {
    return `${Goto.createAgentRoute(agentId)}/threads`;
  }

  /** Redirect route that forks ``threadId`` into a new private,
   *  caller-owned thread and opens it. Use this when building a shareable
   *  "fork this thread" link rather than hardcoding the ``/fork`` suffix. */
  public static createForkAgentThreadRoute(threadId: string): string {
    return `${Goto.route.AgentThreads}/${threadId}/fork`;
  }

  /** Launch form for an agent. ``datasetId`` pre-fills the agent's
   *  ``DATASET_ID`` variable, the handoff the dataset page's "Run agent"
   *  affordances use. */
  public static createAgentLaunchRoute(
    agentId: string,
    params: { datasetId?: string } = {},
  ): string {
    const base = `${Goto.createAgentRoute(agentId)}/launch`;
    return params.datasetId
      ? `${base}?dataset_id=${encodeURIComponent(params.datasetId)}`
      : base;
  }

  /**
   * The org-level metrics page: `/metrics`, or `/metrics/{dashboardId}` when a
   * saved dashboard is selected. The active saved-dashboard id is a route path
   * segment, never a query param; other dashboard view-state (`range`,
   * `period`, `tab`) stays in the query string and is appended by callers. A
   * `null`/`undefined` id means "no dashboard selected" and yields the bare
   * route.
   */
  public static createMetricsRoute(dashboardId?: string | null): string {
    return dashboardId
      ? `${Goto.route.Metrics}/${encodeURIComponent(dashboardId)}`
      : Goto.route.Metrics;
  }

  /**
   * A device's Metrics tab: `/devices/{deviceId}/metrics`, or
   * `/devices/{deviceId}/metrics/{dashboardId}` when a saved dashboard is
   * selected. A `null`/`undefined` id means "no dashboard selected" and yields
   * the bare tab.
   */
  public static createDeviceMetricsRoute(
    deviceId: string,
    dashboardId?: string | null,
  ): string {
    const base = `${Goto.route.Devices}/${encodeURIComponent(deviceId)}/metrics`;
    return dashboardId ? `${base}/${encodeURIComponent(dashboardId)}` : base;
  }

  /**
   * The metrics-dashboard route for one dashboard context: the device-scoped
   * Metrics tab when `deviceId` is set, the org-level page otherwise. This is
   * the builder `MetricsDashboard` itself navigates with when the active
   * dashboard changes — its `deviceId` prop is exactly the signal that
   * distinguishes its two routed mounts.
   */
  public static createMetricsDashboardRoute(opts: {
    deviceId?: string;
    dashboardId: string | null;
  }): string {
    return opts.deviceId
      ? Goto.createDeviceMetricsRoute(opts.deviceId, opts.dashboardId)
      : Goto.createMetricsRoute(opts.dashboardId);
  }

  /**
   * The widget editor for one dashboard context: creating a widget when
   * `cardId` is absent, editing that card when it is present.
   *
   * `dashboardId` is null only for the unsaved view, which has no record. A
   * saved dashboard MUST name itself: the id tells a reloaded editor which
   * dashboard the widget was joining. The draft itself travels in the query
   * string, which callers append (see `WIDGET_DRAFT_PARAMS`).
   */
  public static createMetricsWidgetRoute(opts: {
    deviceId?: string;
    dashboardId: string | null;
    cardId?: string;
  }): string {
    const dashboard = Goto.createMetricsDashboardRoute({
      deviceId: opts.deviceId,
      dashboardId: opts.dashboardId,
    });
    // Substituted rather than run through `generatePath`, which passes params
    // through verbatim: an id holding a `/` would silently become two path
    // segments and match no route.
    const tail =
      opts.cardId === undefined
        ? WIDGET_ROUTE_TAIL.create
        : WIDGET_ROUTE_TAIL.edit.replace(
            ":cardId",
            encodeURIComponent(opts.cardId),
          );
    return `${dashboard}/${tail}`;
  }

  /** The device detail page. Declarative twin of {@link device}. */
  public static createDeviceRoute(deviceId: string): string {
    return `${Goto.route.Devices}/${encodeURIComponent(deviceId)}`;
  }

  /** The device's Configurations tab, open at `directoryPath` when one is given. */
  public static createDeviceConfigurationsRoute(
    deviceId: string,
    directoryPath?: string,
  ): string {
    return withDirectoryPath(
      `${Goto.createDeviceRoute(deviceId)}/configurations`,
      directoryPath,
    );
  }

  /** The device type page, at `tab` (a `DeviceTypeTab` segment) when one is given. */
  public static createDeviceTypeRoute(
    deviceTypeId: string,
    tab?: string,
  ): string {
    const page = `${Goto.route.DeviceTypes}/${encodeURIComponent(deviceTypeId)}`;
    return tab === undefined ? page : `${page}/${tab}`;
  }

  /** The Assets tab of Settings, open at `directoryPath` when one is given. */
  public static createOrganizationAssetsRoute(directoryPath?: string): string {
    return withDirectoryPath(Goto.route.Settings.Assets, directoryPath);
  }

  /** The session detail page. Declarative twin of {@link session}. */
  public static createSessionRoute(sessionId: string): string {
    return `${Goto.route.Sessions}/${sessionId}`;
  }

  /**
   * Route to the admin explorer for one bring-your-own bucket, optionally at a folder.
   *
   * The prefix rides in the query string rather than the path so its slashes survive a round
   * trip: a path segment would have to be re-encoded per segment and decoded on the way back.
   */
  public static createS3BucketExplorerRoute(
    bucketName: string,
    prefix?: string,
  ) {
    const base = `${Goto.route.Settings.Integrations.S3}/${encodeURIComponent(bucketName)}`;
    if (prefix === undefined || prefix.length === 0) {
      return base;
    }
    return `${base}?prefix=${encodeURIComponent(prefix)}`;
  }

  public static createDatasetRoute(datasetId: string, directoryPath?: string) {
    if (directoryPath) {
      return `${Goto.route.Datasets}/${datasetId}/files/${encodePath(directoryPath)}`;
    }
    return `${Goto.route.Datasets}/${datasetId}`;
  }

  /**
   * The page that shows the files `associationId` holds, open at
   * `directoryPath` when one is given.
   */
  public static createAssociationRoute(
    associationId: string,
    directoryPath?: string,
  ): string {
    switch (fileAssociationType(associationId)) {
      case AssociationType.Org:
        // Settings shows the current org's files only, so the link carries
        // the org and goes through the route that checks it.
        return withDirectoryPath(
          `${Goto.route.OrganizationAssetsById}/${associationId}`,
          directoryPath,
        );
      case AssociationType.Device:
        // A device's association ID is its `dv_` ID, not the `device_id` its
        // page is addressed by, so it goes through the redirect route.
        return withDirectoryPath(
          `${Goto.route.DeviceConfigurationsByUniversalId}/${associationId}`,
          directoryPath,
        );
      case AssociationType.DeviceType:
        return withDirectoryPath(
          Goto.createDeviceTypeRoute(associationId, "configurations"),
          directoryPath,
        );
      case AssociationType.Dataset:
        return Goto.createDatasetRoute(associationId, directoryPath);
    }
  }

  /**
   * Creates a redirect URL for a roboto:// URI.
   *
   * This is useful for generating shareable links that work outside the app
   * (e.g., in GitHub issues, Slack, JIRA) and redirect to the appropriate
   * page when clicked.
   *
   * @param robotoUri - A roboto:// URI (e.g., "roboto://dataset/ds_123")
   * @returns A relative URL path (e.g., "/r?uri=roboto%3A%2F%2Fdataset%2Fds_123")
   */
  public static createRedirectUrl(robotoUri: string): string {
    return `${Goto.route.RobotoUriRedirect}?uri=${encodeURIComponent(robotoUri)}`;
  }

  public association({ associationId, directoryPath }: AssociationRouteParams) {
    void this.#navigate(
      Goto.createAssociationRoute(associationId, directoryPath),
    );
  }

  public dataset({ datasetId, directoryPath }: DatasetRouteParams) {
    const path = Goto.createDatasetRoute(datasetId, directoryPath);
    void this.#navigate(path);
  }

  public datasetTopics({ datasetId }: DatasetRouteParams) {
    void this.#navigate(`${Goto.route.Datasets}/${datasetId}/topics`);
  }

  public datasetEvents({ datasetId }: DatasetRouteParams) {
    void this.#navigate(`${Goto.route.Datasets}/${datasetId}/events`);
  }

  public datasetActions({ datasetId }: DatasetRouteParams) {
    void this.#navigate(`${Goto.route.Datasets}/${datasetId}/actions`);
  }

  public datasetDetails({ datasetId }: DatasetRouteParams) {
    void this.#navigate(`${Goto.route.Datasets}/${datasetId}/details`);
  }

  public datasetPermissions({ datasetId }: DatasetRouteParams) {
    void this.#navigate(`${Goto.route.Datasets}/${datasetId}/permissions`);
  }

  public createDataset() {
    void this.#navigate(`${Goto.route.CreateDataset}`);
  }

  public createDatasetEvent({ datasetId }: DatasetRouteParams) {
    void this.#navigate(`${Goto.route.Datasets}/${datasetId}/events/create`);
  }

  public actions(params: ActionsRouteParam = {}) {
    switch (params.tab) {
      case "hub":
        void this.#navigate(Goto.route.ActionHub);
        break;
      case "invocations":
        void this.#navigate(Goto.route.Invocations);
        break;
      case "images":
        void this.#navigate(Goto.route.ActionImages);
        break;
      default:
        void this.#navigate(Goto.route.Actions);
    }
  }

  public action({ action }: ActionRouteParams) {
    const urlParts: string[] = [Goto.route.Actions];
    if (action.owner) {
      urlParts.push(action.owner);
    }
    if (action.digest) {
      urlParts.push(`${action.name}?digest=${action.digest}`);
    } else {
      urlParts.push(action.name);
    }
    void this.#navigate(urlParts.join("/"));
  }

  public createAction() {
    void this.#navigate(Goto.route.CreateAction);
  }

  public static createInvocationRoute(invocationId: string): string {
    return `${Goto.route.Invocations}/${invocationId}`;
  }

  public invocation({ invocationId, tab }: InvocationRouteParams) {
    const route = Goto.createInvocationRoute(invocationId);
    void this.#navigate(tab ? `${route}?tab=${tab}` : route);
  }

  public invokeAction(params: InvokeActionRouteParams = {}) {
    const { action, datasetId, baseInvocation, inputFiles } = params;
    const urlParams = new URLSearchParams();

    if (action) {
      // Add "action_digest" to query params when we want to
      // enable invoking a specific version of an action
      urlParams.append("action_name", action.name);
      if (action.owner) {
        urlParams.append("action_owner", action.owner);
      }
    }
    if (datasetId) {
      urlParams.append("dataset_id", datasetId);
    }
    if (baseInvocation) {
      urlParams.append("base_invocation", baseInvocation);
    }

    if (inputFiles) {
      const encodedInputFiles = inputFiles
        .map((item) => encodeURIComponent(item))
        .join(",");
      urlParams.append("input_files", encodedInputFiles);
    }

    const queryString = urlParams.toString();
    const href = queryString
      ? `${Goto.route.InvokeAction}?${urlParams.toString()}`
      : `${Goto.route.InvokeAction}`;

    void this.#navigate(href);
  }

  public trigger({ triggerId }: TriggerRouteParams) {
    void this.#navigate(Goto.createTriggerRoute(triggerId));
  }

  /**
   * One trigger's page. The page is addressed by name and resolves a `tr_` id
   * to its name, so either works here.
   */
  public static createTriggerRoute(triggerId: string): string {
    return `${Goto.route.Triggers}/${encodeURIComponent(triggerId)}`;
  }

  public editTrigger({ triggerId }: TriggerRouteParams) {
    void this.#navigate(Goto.createEditTriggerRoute(triggerId));
  }

  /** Where one trigger is edited, which its details page links to. */
  public static createEditTriggerRoute(triggerId: string): string {
    return `${Goto.route.Triggers}/${encodeURIComponent(triggerId)}/edit`;
  }

  public triggers() {
    void this.#navigate(Goto.route.Triggers);
  }

  public createTrigger() {
    void this.#navigate(Goto.route.CreateTrigger);
  }

  /** One ingestion rule's page. */
  public static createIngestionRuleRoute(ruleId: string): string {
    return `${Goto.route.Settings.Ingestion.Rules}/${encodeURIComponent(ruleId)}`;
  }

  /** Where one ingestion rule is edited. */
  public static createEditIngestionRuleRoute(ruleId: string): string {
    return `${Goto.createIngestionRuleRoute(ruleId)}/edit`;
  }

  /**
   * The new-rule form, optionally prefilled. The prefill rides in the search
   * params so the link can be shared.
   */
  public static createNewIngestionRuleRoute(
    prefill?: IngestionRulePrefill,
  ): string {
    const path = `${Goto.route.Settings.Ingestion.Rules}/new`;
    const search =
      prefill === undefined
        ? ""
        : rulePrefillToSearchParams(prefill).toString();
    return search === "" ? path : `${path}?${search}`;
  }

  public collections() {
    void this.#navigate(Goto.route.Collections);
  }

  public collection({ collectionId }: CollectionRouteParams) {
    void this.#navigate(`${Goto.route.Collections}/${collectionId}`);
  }

  public device({ deviceId }: DeviceRouteParams) {
    void this.#navigate(Goto.createDeviceRoute(deviceId));
  }

  public agentThread(
    { threadId }: AgentThreadRouteParams,
    options?: { replace?: boolean },
  ) {
    // ``replace`` is for redirect routes (e.g. the fork-and-open route)
    // that should not leave their own URL in history — otherwise the
    // browser back button would land back on the redirect and re-run its
    // side effect (here, creating a second fork).
    void this.#navigate(`${Goto.route.AgentThreads}/${threadId}`, options);
  }

  public agentThreads() {
    void this.#navigate(Goto.route.AgentThreads);
  }

  public session({ sessionId }: SessionRouteParams) {
    void this.#navigate(`${Goto.route.Sessions}/${sessionId}`);
  }

  public sessions() {
    void this.#navigate(Goto.route.Sessions);
  }

  /**
   * `datasetId` alone seeds the create page's file selection with every
   * file in that dataset. A pre-selection replaces that seed: `fileIds`
   * pre-seeds explicitly selected files and is valid with or without
   * `datasetId`, while `directoryPatterns` requires `datasetId` to scope
   * its expansion (see {@link CreateSessionRouteParams}).
   */
  public createSession(params: CreateSessionRouteParams = {}) {
    const { datasetId, fileIds, directoryPatterns } = params;
    const path = datasetId
      ? `${Goto.route.CreateSession}?dataset_id=${encodeURIComponent(datasetId)}`
      : Goto.route.CreateSession;
    const state: CreateSessionLocationState = {};
    if (fileIds && fileIds.length > 0) {
      state.fileIds = fileIds;
    }
    if (directoryPatterns && directoryPatterns.length > 0) {
      state.directoryPatterns = directoryPatterns;
    }
    void this.#navigate(
      path,
      Object.keys(state).length > 0 ? { state } : undefined,
    );
  }

  public file({ fileId, params }: FileRouteParams) {
    window.scrollTo(0, 0); // Ensure the page doesn't load scrolled down

    if (params) {
      const { layout, t } = params;
      const searchParams = new URLSearchParams();

      if (layout) {
        layoutNavigationState.set(layout);
      }

      if (t) {
        // The workspace expects the timestamp to be encoded as a fixed-point seconds value.
        searchParams.set(SearchParams.TIMESTAMP, nanoSecToSecStr(t));
      }

      const hasSearchParams = searchParams.size > 0;

      void this.#navigate(
        `${Goto.route.Files}/${fileId}${hasSearchParams ? `?${searchParams}` : ""}`,
      );
    } else {
      void this.#navigate(`${Goto.route.Files}/${fileId}`);
    }
  }

  public visualize({ dataSource, params, state }: VisualizerRouteParams) {
    const searchParams = new URLSearchParams();

    // Each plural field becomes one search param per id; the route reads them
    // all back with `getAll`.
    const appendEach = (param: string, ids?: string[]) =>
      ids?.forEach((id) => searchParams.append(param, id));

    if (dataSource?.dataset) {
      searchParams.append(SearchParams.DATASET, dataSource.dataset);
    }
    appendEach(SearchParams.DATASET, dataSource?.datasets);
    appendEach(SearchParams.FILE, dataSource?.files);
    appendEach(SearchParams.SESSION, dataSource?.sessions);

    if (params) {
      const { event, layout, layoutId, t } = params;

      if (event) {
        searchParams.set(SearchParams.EVENT, event);
      }

      if (layout) {
        layoutNavigationState.set(layout);
      }

      if (layoutId) {
        searchParams.set(SearchParams.LAYOUT_ID, layoutId);
      }

      if (t) {
        // The workspace expects the timestamp to be encoded as a fixed-point seconds value.
        searchParams.set(SearchParams.TIMESTAMP, nanoSecToSecStr(t));
      }
    }

    window.scrollTo(0, 0); // Ensure the page doesn't load scrolled down
    const hasSearchParams = searchParams.size > 0;
    void this.#navigate(
      `${Goto.route.Visualize}${hasSearchParams ? `?${searchParams}` : ""}`,
      { state },
    );
  }

  public secretsList() {
    void this.#navigate(Goto.route.Settings.Secrets);
  }
}

export type PageRoute = (typeof Goto.route)[keyof typeof Goto.route];

export type ExternalRoute = `https://${string}`;
